import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, FileText, Plane, Loader2 } from "lucide-react";
import { SERVICE_FEES, formatGHS } from "@/data/content";

export default function NewApplication() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ service_type: "visa", title: "", destination: "", travel_date: "", details: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/applications", form);
      toast.success("Application submitted!");
      navigate(`/applications/${data.id}`);
    } catch (err) {
      toast.error("Could not submit application.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-navy text-brand-text">
      <Navbar variant="app" />
      <main className="max-w-2xl mx-auto px-6 pt-28 pb-20">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-white/50 hover:text-brand-gold mb-8 text-sm" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <h1 className="font-display text-4xl tracking-tight mb-2">New Application</h1>
        <p className="text-white/50 mb-8">Tell us what you need and we'll take it from here.</p>

        <form onSubmit={submit} className="space-y-6 p-8 rounded-2xl bg-brand-surface border border-white/10">
          <div>
            <Label className="text-white/70 mb-3 block">Service Type</Label>
            <div className="grid grid-cols-2 gap-4">
              {[{ v: "visa", label: "Visa / Documents", icon: FileText }, { v: "tour", label: "Tour / Booking", icon: Plane }].map((o) => (
                <button key={o.v} type="button" onClick={() => setForm({ ...form, service_type: o.v })} data-testid={`service-type-${o.v}`}
                  className={`p-4 rounded-xl border flex items-center gap-3 transition-colors ${form.service_type === o.v ? "border-brand-gold bg-brand-gold/10 text-brand-gold" : "border-white/10 text-white/60 hover:border-white/25"}`}>
                  <o.icon className="w-5 h-5" /> {o.label}
                  <span className="ml-auto text-xs opacity-80">{formatGHS(SERVICE_FEES[o.v])}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="title" className="text-white/70">Application Title</Label>
            <Input id="title" required value={form.title} onChange={set("title")} data-testid="app-title-input"
              placeholder={form.service_type === "visa" ? "e.g. UK Tourist Visa" : "e.g. Dubai 5-Day Package"}
              className="mt-2 bg-brand-navy border-white/10 text-white h-11" />
          </div>

          <div>
            <Label htmlFor="destination" className="text-white/70">Destination Country</Label>
            <Input id="destination" required value={form.destination} onChange={set("destination")} data-testid="app-destination-input"
              placeholder="e.g. United Kingdom" className="mt-2 bg-brand-navy border-white/10 text-white h-11" />
          </div>

          <div>
            <Label htmlFor="travel_date" className="text-white/70">Intended Travel Date</Label>
            <Input id="travel_date" type="date" value={form.travel_date} onChange={set("travel_date")} data-testid="app-date-input"
              className="mt-2 bg-brand-navy border-white/10 text-white h-11" />
          </div>

          <div>
            <Label htmlFor="details" className="text-white/70">Additional Details</Label>
            <Textarea id="details" value={form.details} onChange={set("details")} data-testid="app-details-input"
              placeholder="Tell us more about your trip or requirements…" className="mt-2 bg-brand-navy border-white/10 text-white min-h-[110px]" />
          </div>

          <Button type="submit" disabled={loading} data-testid="app-submit-btn"
            className="w-full h-11 rounded-full bg-brand-gold text-brand-navy hover:bg-brand-goldhover font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Application"}
          </Button>
        </form>
      </main>
    </div>
  );
}
